<template>
  <div class="tagging-page">
    <div class="tagging-header">
      <h1>Tagging</h1>
      <p class="stats" v-if="releases.length">
        {{ flagged.length }} of {{ releases.length }} records need tags
      </p>
    </div>

    <div v-if="isLoading" class="status">Loading your collection…</div>
    <div v-else-if="error" class="status error">{{ error }}</div>
    <p v-else-if="!flagged.length" class="status">Every record has genre tags and an original year.</p>

    <div v-else class="flagged-list">
      <article v-for="{ record, missing } in flagged" :key="record.instanceId" class="flagged-card">
        <img :src="record.coverImage" :alt="record.title" class="cover" loading="lazy">

        <div class="card-body">
          <h3>{{ record.title }}</h3>
          <p class="artist">{{ record.artists.join(', ') }}</p>
          <p class="missing">
            Missing:
            <span v-if="missing.genres" class="gap">genre tags</span>
            <span v-if="missing.year" class="gap">original year</span>
          </p>

          <template v-if="stateFor(record.instanceId).suggestion">
            <p class="reason">{{ stateFor(record.instanceId).suggestion.reason }}</p>

            <div v-if="missing.genres" class="chips" role="group" aria-label="Genre tags">
              <button
                v-for="genre in stateFor(record.instanceId).suggestion.options"
                :key="genre"
                type="button"
                class="chip"
                :class="{ active: stateFor(record.instanceId).genres.includes(genre) }"
                :aria-pressed="stateFor(record.instanceId).genres.includes(genre)"
                @click="toggleGenre(record.instanceId, genre)"
              >{{ genre }}</button>
            </div>

            <p v-if="missing.genres && stateFor(record.instanceId).genres.length" class="reason">
              Filed in folder: <strong>{{ stateFor(record.instanceId).genres[0] }}</strong>
              (the first genre picked)
            </p>

            <label v-if="missing.year" class="year-field">
              Original year
              <input
                type="number"
                min="1900"
                :value="stateFor(record.instanceId).year"
                @input="setYear(record.instanceId, $event.target.value)"
              >
            </label>

            <button
              type="button"
              class="action primary"
              :disabled="!canApply(record, missing) || stateFor(record.instanceId).status === 'saving'"
              @click="apply(record)"
            >
              {{ stateFor(record.instanceId).status === 'saving' ? 'Saving…' : 'Apply to Discogs' }}
            </button>
          </template>

          <button
            v-else
            type="button"
            class="action"
            :disabled="stateFor(record.instanceId).status === 'loading'"
            @click="suggest(record)"
          >
            {{ stateFor(record.instanceId).status === 'loading' ? 'Looking up…' : 'Suggest tags' }}
          </button>

          <p v-if="stateFor(record.instanceId).status === 'error'" class="status error" role="alert">
            {{ stateFor(record.instanceId).message }}
          </p>
        </div>
      </article>
    </div>
  </div>
</template>

<script setup>
import { onMounted } from 'vue';
import useTagging from '@/composables/useTagging';

const {
  releases, flagged, isLoading, error, load, stateFor, suggest, toggleGenre, setYear, apply,
} = useTagging();

// Apply is only offered once the fields that are missing have usable values
const canApply = (record, missing) => {
  const state = stateFor(record.instanceId);
  const year = Number(state.year);
  return (!missing.genres || (state.genres.length >= 1 && state.genres.length <= 2))
    && (!missing.year || (Number.isInteger(year) && year >= 1900));
};

onMounted(load);
</script>

<style scoped>
.tagging-page {
  max-width: var(--page-max);
  margin: 0 auto;
  padding: var(--space-lg) var(--page-gutter) var(--space-xl);
  color: var(--color-text);
}

.tagging-header {
  display: flex;
  align-items: baseline;
  gap: var(--space-md);
  margin-bottom: var(--space-lg);
}

.tagging-header h1 {
  font-size: var(--font-size-xl);
  margin: 0;
}

.stats,
.status {
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.status.error {
  color: #ff6b6b;
}

.flagged-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: var(--space-lg);
}

.flagged-card {
  display: flex;
  gap: var(--space-md);
  background: var(--color-surface);
  border-radius: var(--border-radius);
  padding: var(--space-md);
  box-shadow: var(--shadow-card);
}

.cover {
  width: 96px;
  height: 96px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
}

.card-body {
  min-width: 0;
  flex: 1;
}

.card-body h3 {
  margin: 0 0 var(--space-xs);
  font-size: var(--font-size-md);
}

.artist,
.missing,
.reason {
  margin: 0 0 var(--space-xs);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.gap {
  margin-left: var(--space-xs);
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(255, 107, 107, 0.15);
  color: #ff6b6b;
  font-size: var(--font-size-xs);
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-xs);
  margin: var(--space-sm) 0;
}

.chip {
  background: var(--color-surface-light);
  color: var(--color-text);
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: var(--font-size-xs);
  cursor: pointer;
}

.chip:hover {
  background: var(--color-surface-lighter);
}

.chip.active {
  background: var(--color-primary);
  color: #000;
}

.year-field {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin: var(--space-sm) 0;
  font-size: var(--font-size-sm);
}

.year-field input {
  width: 96px;
  background: var(--color-surface-light);
  color: var(--color-text);
  border: 1px solid var(--color-divider);
  border-radius: 4px;
  padding: 4px 8px;
}

.action {
  background: var(--color-surface-light);
  color: var(--color-text);
  border: none;
  border-radius: 999px;
  padding: 6px 16px;
  font-size: var(--font-size-sm);
  cursor: pointer;
}

.action.primary {
  background: var(--color-primary);
  color: #000;
  font-weight: 700;
}

.action:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
